import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import ArrowBottomIcon from "../../Components/Icon-SVG/ArrowBottomIcon";
import ArrowRightIcon from "../../Components/Icon-SVG/ArrowRightIcon";
import DownloadIcon from "../../Components/Icon-SVG/DownloadIcon";
import SelectLogo from "../../Components/PagesComponents/ReportExport/SelectLogo/SelectLogo";
import Backdrop from "../../Components/UI/Backdrop/Backdrop";
import Chart from "../../Components/common/Chart/Chart";

import ClamCloudMl from "../../Components/PagesComponents/AnalysisReports/ClamCloudMlResults/ClamCloudMl";
import APKInformationCard from "../../Components/PagesComponents/AnalysisReports/APKInformationCard/APKInformationCard";
import CrimesFlat from "../AnalysisReports/AndroidAnalysisReports/Content/Tabs/Crimes/CrimesFlat";
import StaticCodeIssuesFlat from "../AnalysisReports/AndroidAnalysisReports/Content/Tabs/StaticCodeIssues/StaticCodeIssuesFlat";
import PermissionsFlat from "../AnalysisReports/AndroidAnalysisReports/Content/Tabs/Permissions/PermissionsFlat";
import Matrix from "../AnalysisReports/Content/Tabs/Matrix/Matrix";
import StaticInfo from "../AnalysisReports/Content/Tabs/StaticInfo/StaticInfo";
import SnortSuricata from "../AnalysisReports/Content/Tabs/SnortSuricata/SnortSuricata";
import Map from "../../Components/common/Map/Map";
import AndroidPluginsFlat from "../../Components/PagesComponents/AnalysisReports/AndroidPlugins/AndroidPluginsFlat";
import IocsFlat from "../../Components/PagesComponents/AnalysisReports/MemoryBlock/Tabs/IOCs/IocsFlat";

import {
  RequestApkInfo,
  RequestCrimes,
  RequestPermissions,
  RequestPlugins,
  RequestStaticCodeIssues,
} from "../../store/actions/androidActions";
import {
  RequestFilesInfo,
  RequestGeneralAnalysis,
  RequestIocs,
  RequestNetwork,
} from "../../store/actions/analysisReportActions";
import { getAndroidCsvData, getAndroidReportFields } from "./utils/dataSanitization";
import { MA360_REPORT_LOGO } from "../../utils/constants/reportLogos";

import "./styles.scss";

//GET ANDROID REPORT ID IN URL
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const downloadFile = (content, type, fileName) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const AndroidReportExport = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  let query = useQuery();

  const originalID = query.get("data");
  const stateParam = query.get("state");

  const general = useSelector((state) => state.analysisReports.general);
  const loadingGeneral = useSelector((state) => state.analysisReports.loadingGeneral);
  const filesInfo = useSelector((state) => state.analysisReports.filesInfo);
  const networkActivityMap = useSelector((state) => state.analysisReports.networkActivityMap);
  const snortSuricataAlerts = useSelector((state) => state.analysisReports.snortSuricataAlerts);
  const iocs = useSelector((state) => state.analysisReports.iocs);

  const apkInfo = useSelector((state) => state.android.apkInfo);
  const crimes = useSelector((state) => state.android.crimes);
  const staticCodeIssues = useSelector((state) => state.android.staticCodeIssues);
  const permissions = useSelector((state) => state.android.permissions);
  const plugins = useSelector((state) => state.android.plugins);

  const [generalTab, setGeneralTab] = useState(true);
  const [apkTab, setApkTab] = useState(true);
  const [crimesTab, setCrimesTab] = useState(true);
  const [issuesTab, setIssuesTab] = useState(true);
  const [permissionsTab, setPermissionsTab] = useState(true);
  const [mitreTab, setMitreTab] = useState(true);
  const [staticResultsTab, setStaticResultsTab] = useState(true);
  const [networkTab, setNetworkTab] = useState(true);
  const [networkAlertsTab, setNetworkAlertsTab] = useState(true);
  const [pluginsTab, setPluginsTab] = useState(true);
  const [iocsTab, setIocsTab] = useState(true);

  useEffect(() => {
    // The Android report has no aggregate endpoint, so the export page repeats
    // the requests the analysis page makes. Left out: screenshots (the video
    // cannot be rasterized) and additional details (the section was dropped for
    // report size). Added: filesInfo, for the md5/sha1/ssdeep the General
    // section prints. Matrix and StaticInfo dispatch for themselves off the
    // same ?data= param.
    dispatch(RequestGeneralAnalysis({ parentID: originalID, clickedFileID: originalID }));
    dispatch(RequestNetwork({ parentID: originalID, clickedFileID: originalID }));
    dispatch(RequestIocs({ parentID: originalID, clickedFileID: originalID }));
    dispatch(RequestFilesInfo(originalID));
    dispatch(RequestApkInfo(originalID));
    dispatch(RequestCrimes(originalID));
    dispatch(RequestStaticCodeIssues(originalID));
    dispatch(RequestPermissions(originalID));
    dispatch(RequestPlugins(originalID));
  }, [dispatch, originalID]);

  const fileName = general?.file_name || "Android";
  const targetFile = (Array.isArray(filesInfo) ? filesInfo : [])[0]?.target?.file || {};

  const reportOptions = {
    general,
    apkInfo,
    filesInfo,
    crimes,
    staticCodeIssues,
    permissions,
    plugins,
    iocs,
    networkActivityMap,
    snortSuricataAlerts,
    stateParam,
  };

  const handleGeneratePdf = () => {
    history.push(
      `/android-report-pdf?data=${originalID}&state=${stateParam}&g=${generalTab}&k=${apkTab}&c=${crimesTab}&i=${issuesTab}&p=${permissionsTab}&m=${mitreTab}&r=${staticResultsTab}&n=${networkTab}&w=${networkAlertsTab}&u=${pluginsTab}&o=${iocsTab}`,
    );
  };

  const handleGenerateJson = () => {
    downloadFile(
      JSON.stringify(getAndroidReportFields(reportOptions), null, 2),
      "application/json",
      "Report - " + fileName + ".json",
    );
  };

  const handleGenerateCsv = () => {
    downloadFile(getAndroidCsvData(reportOptions), "text/csv", "Report - " + fileName + ".csv");
  };

  // general initialises to [] rather than null, so a plain truthiness check
  // would let the first render through with an empty report.
  if (loadingGeneral || !general || Object.keys(general).length === 0) {
    return <Backdrop show={true} absolute={true} message="Loading report..." />;
  }

  return (
    <div className="android-report">
      <div className="android-report-header">
        <h1>Report Generate</h1>
        <p>
          This section allows you to preview the report and export it in various formats. Please,
          collapse the sections not to view the details in the report.
        </p>
      </div>
      <div className="android-report-body">
        <div className="android-report-main">
          <div className="android-report-main__content">
            <div className="android-report-main__content__header">
              <SelectLogo defaultLogo={MA360_REPORT_LOGO} />
              <div className="report-actions__buttons__container">
                <div
                  className="report-actions__buttons__container--export primary-button"
                  onClick={handleGeneratePdf}>
                  <div>Export to PDF</div>
                  <DownloadIcon color="var(--header-search-text-color)" width={16} height={16} />
                </div>
                <div
                  className="report-actions__buttons__container--export primary-button"
                  onClick={handleGenerateJson}>
                  Export to JSON
                  <DownloadIcon color="var(--header-search-text-color)" width={16} height={16} />
                </div>
                <div
                  className="report-actions__buttons__container--export primary-button"
                  onClick={handleGenerateCsv}>
                  Export to CSV
                  <DownloadIcon color="var(--header-search-text-color)" width={16} height={16} />
                </div>
              </div>
            </div>

            <div className="android-report-main__content--general">
              <h2 onClick={() => setGeneralTab(!generalTab)}>
                General
                <span>
                  {generalTab ? (
                    <ArrowBottomIcon width={16} height={16} />
                  ) : (
                    <ArrowRightIcon width={16} height={16} />
                  )}
                </span>
              </h2>
              <div
                className={`android-report-main__content--general-content ${
                  generalTab ? "visible" : "hidden"
                }`}>
                <table>
                  <tbody>
                    <tr>
                      <td>Target</td>
                      <td>: {general?.file_name}</td>
                    </tr>
                    <tr>
                      <td>Size</td>
                      <td>: {general?.size}</td>
                    </tr>
                    <tr>
                      <td>MD5</td>
                      <td>: {targetFile.md5}</td>
                    </tr>
                    <tr>
                      <td>SHA1</td>
                      <td>: {targetFile.sha1}</td>
                    </tr>
                    <tr>
                      <td>SHA256</td>
                      <td>: {general?.sha1}</td>
                    </tr>
                    <tr>
                      <td>SSDEEP</td>
                      <td>: {targetFile.ssdeep}</td>
                    </tr>
                  </tbody>
                </table>
                <div className="android-report-main__content--general-content_score">
                  <div className="android-report-main__content--general-content_score-verdict">
                    <span
                      className={
                        general?.isApt
                          ? "analysis-reports__header-verdict analysis-reports__header-verdict--apt"
                          : stateParam === "malicious"
                            ? "analysis-reports__header-verdict analysis-reports__header-verdict--malicious"
                            : stateParam === "not suspicious"
                              ? "analysis-reports__header-verdict analysis-reports__header-verdict--not-suspicious"
                              : "analysis-reports__header-verdict analysis-reports__header-verdict--suspicious"
                      }>
                      {general?.isApt ? <span>malicious</span> : stateParam}
                    </span>
                  </div>
                  <div className="android-report-main__content--general-content_score-chart analysis-reports__header-doughnutChart">
                    <Chart
                      name="doughnut-dark"
                      type="doughnut"
                      fileState={stateParam}
                      decision_score={general?.decision_score || "-"}
                    />
                  </div>
                  <ClamCloudMl report={true} />
                </div>
              </div>
            </div>

            <div className="android-report-main__content--apk">
              <h2 onClick={() => setApkTab(!apkTab)}>
                APK Information
                <span>
                  {apkTab ? (
                    <ArrowBottomIcon width={16} height={16} />
                  ) : (
                    <ArrowRightIcon width={16} height={16} />
                  )}
                </span>
              </h2>
              <div
                className={`android-report-main__content--apk-content ${
                  apkTab ? "visible" : "hidden"
                }`}>
                <APKInformationCard report={true} />
              </div>
            </div>

            <div className="android-report-main__content--crimes">
              <h2 onClick={() => setCrimesTab(!crimesTab)}>
                Possible Malicious Actions
                <span>
                  {crimesTab ? (
                    <ArrowBottomIcon width={16} height={16} />
                  ) : (
                    <ArrowRightIcon width={16} height={16} />
                  )}
                </span>
              </h2>
              <div
                className={`android-report-main__content--crimes-content ${
                  crimesTab ? "visible" : "hidden"
                }`}>
                <CrimesFlat data={crimes} />
              </div>
            </div>

            <div className="android-report-main__content--static-code-issues">
              <h2 onClick={() => setIssuesTab(!issuesTab)}>
                Static Code Issues
                <span>
                  {issuesTab ? (
                    <ArrowBottomIcon width={16} height={16} />
                  ) : (
                    <ArrowRightIcon width={16} height={16} />
                  )}
                </span>
              </h2>
              <div
                className={`android-report-main__content--static-code-issues-content ${
                  issuesTab ? "visible" : "hidden"
                }`}>
                <StaticCodeIssuesFlat data={staticCodeIssues} />
              </div>
            </div>

            <div className="android-report-main__content--permissions">
              <h2 onClick={() => setPermissionsTab(!permissionsTab)}>
                Permissions
                <span>
                  {permissionsTab ? (
                    <ArrowBottomIcon width={16} height={16} />
                  ) : (
                    <ArrowRightIcon width={16} height={16} />
                  )}
                </span>
              </h2>
              <div
                className={`android-report-main__content--permissions-content ${
                  permissionsTab ? "visible" : "hidden"
                }`}>
                <PermissionsFlat data={permissions} />
              </div>
            </div>

            <div className="android-report-main__content--mitre">
              <h2 onClick={() => setMitreTab(!mitreTab)}>
                MITRE ATT&CK™ Matrix
                <span>
                  {mitreTab ? (
                    <ArrowBottomIcon width={16} height={16} />
                  ) : (
                    <ArrowRightIcon width={16} height={16} />
                  )}
                </span>
              </h2>
              <div
                className={`android-report-main__content--mitre-content ${
                  mitreTab ? "visible" : "hidden"
                }`}>
                <Matrix report={true} />
              </div>
            </div>

            <div className="android-report-main__content--static-results">
              <h2 onClick={() => setStaticResultsTab(!staticResultsTab)}>
                Static Results
                <span>
                  {staticResultsTab ? (
                    <ArrowBottomIcon width={16} height={16} />
                  ) : (
                    <ArrowRightIcon width={16} height={16} />
                  )}
                </span>
              </h2>
              <div
                className={`android-report-main__content--static-results-content ${
                  staticResultsTab ? "visible" : "hidden"
                }`}>
                <StaticInfo report={true} />
              </div>
            </div>

            <div className="android-report-main__content--network">
              <h2 onClick={() => setNetworkTab(!networkTab)}>
                Network Activity
                <span>
                  {networkTab ? (
                    <ArrowBottomIcon width={16} height={16} />
                  ) : (
                    <ArrowRightIcon width={16} height={16} />
                  )}
                </span>
              </h2>
              <div
                className={`android-report-main__content--network-content ${
                  networkTab ? "visible" : "hidden"
                }`}>
                <Map report={true} />
              </div>
            </div>

            <div className="android-report-main__content--network-alerts">
              <h2 onClick={() => setNetworkAlertsTab(!networkAlertsTab)}>
                Network Alerts
                <span>
                  {networkAlertsTab ? (
                    <ArrowBottomIcon width={16} height={16} />
                  ) : (
                    <ArrowRightIcon width={16} height={16} />
                  )}
                </span>
              </h2>
              <div
                className={`android-report-main__content--network-alerts-content ${
                  networkAlertsTab ? "visible" : "hidden"
                }`}>
                <SnortSuricata report={true} />
              </div>
            </div>

            <div className="android-report-main__content--plugins">
              <h2 onClick={() => setPluginsTab(!pluginsTab)}>
                Plugins
                <span>
                  {pluginsTab ? (
                    <ArrowBottomIcon width={16} height={16} />
                  ) : (
                    <ArrowRightIcon width={16} height={16} />
                  )}
                </span>
              </h2>
              <div
                className={`android-report-main__content--plugins-content ${
                  pluginsTab ? "visible" : "hidden"
                }`}>
                <AndroidPluginsFlat plugins={plugins} />
              </div>
            </div>

            <div className="android-report-main__content--iocs">
              <h2 onClick={() => setIocsTab(!iocsTab)}>
                IOCs
                <span>
                  {iocsTab ? (
                    <ArrowBottomIcon width={16} height={16} />
                  ) : (
                    <ArrowRightIcon width={16} height={16} />
                  )}
                </span>
              </h2>
              <div
                className={`android-report-main__content--iocs-content ${
                  iocsTab ? "visible" : "hidden"
                }`}>
                <IocsFlat iocs={iocs} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AndroidReportExport;
